import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useSession } from '../../contexts/SessionContext';
import RightSlideModal from '../../components/RightSlideModal';
import CommonTable, { TableRowAvatarCell } from '../../components/CommonTable';

export default function SubjectsPage() {
  const [subjects, setSubjects] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClass, setSelectedClass] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [formData, setFormData] = useState({
    subjectName: '',
    subjectCode: '',
    classId: '',
    description: ''
  });
  const { selectedSession, sessions } = useSession();
  const [modalSession, setModalSession] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [subjectsRes, classesRes] = await Promise.all([
        api.get('/subjects'),
        api.get('/classes')
      ]);
      setSubjects(subjectsRes.data);
      setClasses(classesRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedSubject) {
        await api.put(`/subjects/${selectedSubject.id}`, formData);
        toast.success('Subject updated successfully');
      } else {
        await api.post('/subjects', formData);
        toast.success('Subject added successfully');
      }
      setShowModal(false);
      setFormData({ subjectName: '', subjectCode: '', classId: '', description: '' });
      setSelectedSubject(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save subject');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this subject?')) return;
    try {
      await api.delete(`/subjects/${id}`);
      toast.success('Subject deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete subject');
    }
  };

  const filteredSubjects = subjects.filter(subject => {
    const subjectClass = classes.find(c => c.id === subject.classId);
    const sessionMatch = !selectedSession || selectedSession === 'all' || subjectClass?.sessionYear === selectedSession;
    const classMatch = selectedClass === 'all' || subject.classId === selectedClass;
    return sessionMatch && classMatch;
  });

  const colors = [
    'bg-indigo-50 text-indigo-700 border-indigo-200/50',
    'bg-sky-50 text-sky-700 border-sky-200/50',
    'bg-emerald-50 text-emerald-700 border-emerald-200/50',
    'bg-amber-50 text-amber-700 border-amber-200/50',
    'bg-rose-50 text-rose-700 border-rose-200/50',
    'bg-purple-50 text-purple-700 border-purple-200/50'
  ];

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="font-outfit font-semibold text-2xl text-slate-800">Subjects</h2>
          <div className="flex items-center gap-2 mt-1">
            <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-bold ${selectedSession === 'all' ? 'bg-slate-100 text-slate-600' : 'bg-indigo-50 text-indigo-700'}`}>
              Session: {selectedSession === 'all' ? 'All Sessions' : selectedSession}
            </span>
            <div className="h-4 w-px bg-slate-200" />
            <select 
              value={selectedClass} 
              onChange={(e) => setSelectedClass(e.target.value)}
              className="bg-transparent border-none text-xs font-semibold text-slate-500 focus:ring-0 cursor-pointer"
            >
              <option value="all">All Classes</option>
              {classes.filter(c => selectedSession === 'all' || c.sessionYear === selectedSession).map(c => (
                <option key={c.id} value={c.id}>{c.className}</option>
              ))}
            </select>
          </div>
        </div>
        <button
          onClick={() => {
            setSelectedSubject(null);
            setFormData({ subjectName: '', subjectCode: '', classId: '', description: '' });
            setModalSession(selectedSession === 'all' ? '' : selectedSession);
            setShowModal(true);
          }}
          className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full px-6 py-2.5 font-medium shadow-sm transition-all flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Add Subject
        </button>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
        <button
          onClick={() => setSelectedClass('all')}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
            selectedClass === 'all' 
            ? 'bg-indigo-600 text-white shadow-md' 
            : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
          }`}
        >
          All Classes
        </button>
        {classes.map(c => (
          <button
            key={c.id}
            onClick={() => setSelectedClass(c.id)}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
              selectedClass === c.id 
              ? 'bg-indigo-600 text-white shadow-md' 
              : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
            }`}
          >
            {c.className}
          </button>
        ))}
      </div>

      <CommonTable headers={['Subject', 'Class', 'Description', 'Actions']}>
        {filteredSubjects.length > 0 ? (
          filteredSubjects.map((subject, index) => {
            const subjectClass = classes.find(c => c.id === subject.classId);
            const colorClass = colors[index % colors.length];
            return (
              <tr key={subject.id} className="hover:bg-slate-50/50 transition-colors">
                <td className="px-6 py-4">
                  <TableRowAvatarCell
                    title={subject.subjectName}
                    subtitle={subject.subjectCode}
                    colorClass={colorClass}
                  />
                </td>
                <td className="px-6 py-4">
                  <span className="px-3 py-1 bg-indigo-50 text-indigo-700 text-xs font-semibold rounded-full border border-indigo-100/50">
                    {subjectClass?.className || 'N/A'}
                  </span>
                </td>
                <td className="px-6 py-4 text-slate-600 text-sm max-w-xs truncate">
                  {subject.description || <span className="text-slate-400 italic">No description</span>}
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <button 
                      onClick={() => {
                        setSelectedSubject(subject);
                        setFormData({
                          subjectName: subject.subjectName,
                          subjectCode: subject.subjectCode,
                          classId: subject.classId,
                          description: subject.description
                        });
                        setModalSession(subjectClass?.sessionYear || '');
                        setShowModal(true);
                      }}
                      className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={() => handleDelete(subject.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })
        ) : (
          <tr>
            <td colSpan="4" className="text-center py-12 text-slate-500">
              No subjects found for this class.
            </td>
          </tr>
        )}
      </CommonTable>

      {/* Modal */}
      <RightSlideModal 
        isOpen={showModal} 
        onClose={() => setShowModal(false)} 
        title={selectedSubject ? 'Edit Subject' : 'Add New Subject'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Subject Name</label>
            <input 
              type="text" 
              value={formData.subjectName} 
              onChange={(e) => setFormData({ ...formData, subjectName: e.target.value })} 
              className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all outline-none" 
              placeholder="e.g. Mathematics"
              required 
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Subject Code</label>
            <input 
              type="text" 
              value={formData.subjectCode} 
              onChange={(e) => setFormData({ ...formData, subjectCode: e.target.value })} 
              className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all outline-none" 
              placeholder="e.g. MATH101"
              required 
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Academic Session</label>
              <select 
                value={modalSession} 
                onChange={(e) => {
                  setModalSession(e.target.value);
                  setFormData({ ...formData, classId: '' });
                }} 
                className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all outline-none" 
                required
              >
                <option value="">Select Session</option>
                {sessions.map(s => <option key={s.id} value={s.sessionYear}>{s.sessionYear}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Class</label>
              <select 
                value={formData.classId} 
                onChange={(e) => setFormData({ ...formData, classId: e.target.value })} 
                className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all outline-none" 
                required 
                disabled={!modalSession}
              >
                <option value="">Select Class</option>
                {classes.filter(c => c.sessionYear === modalSession).map(c => (
                  <option key={c.id} value={c.id}>{c.className}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Description</label>
            <textarea 
              value={formData.description} 
              onChange={(e) => setFormData({ ...formData, description: e.target.value })} 
              className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all outline-none" 
              rows="3"
              placeholder="Subject details..."
            />
          </div>
          <div className="flex gap-3 pt-4">
            <button type="submit" className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full py-3 font-medium shadow-sm transition-all active:scale-95">
              {selectedSubject ? 'Update Subject' : 'Add Subject'}
            </button>
            <button type="button" onClick={() => setShowModal(false)} className="flex-1 border border-slate-200 hover:bg-slate-50 text-slate-600 rounded-full py-3 font-medium transition-all active:scale-95">
              Cancel
            </button>
          </div>
        </form>
      </RightSlideModal>
    </div>
  );
}
